import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Bell, Bot, CircleDollarSign, FolderGit2, Gauge, KanbanSquare, LogOut, Menu, Moon, Plus, Settings, Sun, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { cn } from "../lib/cn";
import { relTime } from "../lib/format";
import { useIsMobile, useLogout, useMe, useMeta, useTheme } from "../lib/hooks";
import { get } from "../lib/api";
import type { OverviewData } from "../lib/types";
import { CompanyProvider, useCompany } from "../lib/company";
import { NewMissionDialog, NewMissionProvider, useNewMission } from "./NewMission";
import { NotificationsPanel, useUnreadCount } from "./Notifications";
import { Button } from "./ui/button";
import { Chip, Skeleton } from "./ui/primitives";

const NAV = [
  { to: "/", label: "Overview", icon: Gauge, end: true },
  { to: "/projects", label: "Proyectos", icon: FolderGit2 },
  { to: "/missions", label: "Misiones", icon: KanbanSquare },
  { to: "/agents", label: "Agentes", icon: Bot },
  { to: "/costs", label: "Costes", icon: CircleDollarSign },
  { to: "/activity", label: "Actividad", icon: Activity },
  { to: "/settings", label: "Ajustes", icon: Settings },
];

export function Shell() {
  const me = useMe();
  if (me.isLoading) return <div className="p-8"><Skeleton className="h-10 w-64" /></div>;
  if (!me.data) return <Navigate to="/login" replace />;
  return (
    <CompanyProvider>
      <NewMissionProvider>
        <Layout />
        <NewMissionDialog />
      </NewMissionProvider>
    </CompanyProvider>
  );
}

/** Selector global de empresa: afecta a todas las pantallas. */
function CompanySelect({ compact }: { compact?: boolean }) {
  const { companyId, setCompanyId, companies } = useCompany();
  return (
    <select aria-label="Empresa" value={companyId ?? ""} onChange={(e) => setCompanyId(e.target.value || null)}
      className={cn("h-9 cursor-pointer rounded-lg border bg-sf px-2.5 text-[13px] font-medium text-tx", companyId ? "border-ac" : "border-ln2", compact ? "max-w-[112px]" : "max-w-[170px]")}>
      <option value="">Todas las empresas</option>
      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

/** Indicador de estado de sincronización: datos frescos, sincronizando o sin conexión. */
function SyncPill() {
  const qc = useQueryClient();
  const fetching = useIsFetching() > 0;
  const { companyId, qs } = useCompany();
  const q = useQuery({ queryKey: ["overview", companyId], queryFn: () => get<OverviewData>(`/overview${qs()}`), refetchInterval: 8_000 });
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 15_000); return () => clearInterval(t); }, []);
  const offline = q.isError || (typeof navigator !== "undefined" && !navigator.onLine);
  return (
    <button onClick={() => void qc.invalidateQueries()} title="Actualizar ahora" className="flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-ln px-2.5 text-[12px] text-mu hover:text-tx">
      <span className={cn("h-2 w-2 rounded-full", offline ? "bg-er" : fetching ? "pulse bg-in" : "bg-gd")} />
      <span className="max-xl:hidden">{offline ? "Sin conexión con el backend" : fetching ? "Sincronizando…" : `Sincronizado ${relTime(q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null)}`}</span>
    </button>
  );
}

function Layout() {
  const mobile = useIsMobile();
  const { theme, toggle } = useTheme();
  const meta = useMeta();
  const me = useMe();
  const logout = useLogout();
  const { open } = useNewMission();
  const unread = useUnreadCount();
  const [bell, setBell] = useState(false);
  const [more, setMore] = useState(false);
  const loc = useLocation();
  useEffect(() => { setMore(false); setBell(false); }, [loc.pathname]);
  const demo = meta.data?.mode === "demo";
  const canCreate = me.data?.user.role === "admin" || me.data?.user.role === "operator";

  const bellBtn = (
    <button onClick={() => setBell(true)} aria-label={`Notificaciones${unread ? `, ${unread} sin leer` : ""}`} className="relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-mu hover:text-tx">
      <Bell className="h-5 w-5" />
      {unread > 0 && <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-er px-1 text-[10px] font-bold text-bg">{unread > 9 ? "9+" : unread}</span>}
    </button>
  );

  return (
    <div className="flex h-dvh flex-col">
      <header className="safe-t flex shrink-0 items-center justify-between border-b border-ln px-4 md:px-8" style={{ height: mobile ? 56 : 60 }}>
        <div className="flex min-w-0 items-center gap-7">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap font-display text-[18px] font-bold tracking-[-0.02em] text-tx"><span className="h-4 w-4 rounded-[5px] bg-ac" />B-Green Factory</Link>
          {!mobile && (
            <nav className="flex gap-0.5 whitespace-nowrap">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cn("flex h-9 items-center rounded-lg px-3 text-[14px] font-medium text-tx hover:bg-hv", isActive && "bg-rs")}>{n.label}</NavLink>
              ))}
            </nav>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {demo && <Chip tone="warn" className="max-sm:hidden" title="Proveedores simulados: ningún dato es real">DEMO<span className="max-xl:hidden"> · datos simulados</span></Chip>}
          <CompanySelect compact={mobile} />
          <SyncPill />
          {bellBtn}
          {!mobile && (
            <>
              <button onClick={toggle} aria-label="Cambiar tema" className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-mu hover:text-tx">{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</button>
              {canCreate && <Button variant="primary" onClick={() => open()} aria-label="Nueva misión" className="max-xl:px-3"><Plus className="h-4 w-4" /><span className="max-xl:hidden">Nueva misión</span></Button>}
              <button onClick={() => logout.mutate()} aria-label="Cerrar sesión" title={`${me.data?.user.name} · cerrar sesión`} className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-mu hover:text-tx"><LogOut className="h-5 w-5" /></button>
            </>
          )}
        </div>
      </header>

      {demo && mobile && <div className="shrink-0 bg-wn/15 px-4 py-1 text-center text-[12px] text-wn">DEMO · datos simulados, ninguna integración real</div>}
      <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"><Outlet /></main>

      {mobile && (
        <>
          {canCreate && !/^\/missions\/[^/]+/.test(loc.pathname) && (
            <button onClick={() => open()} aria-label="Nueva misión" className="fixed bottom-[76px] right-4 z-20 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-ac text-acx shadow-lg"><Plus className="h-6 w-6" /></button>
          )}
          <nav className="safe-b grid shrink-0 grid-cols-5 border-t border-ln bg-sf" aria-label="Navegación principal">
            {[NAV[0]!, NAV[2]!, NAV[3]!, NAV[4]!].map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cn("flex h-14 flex-col items-center justify-center gap-0.5 border-t-2 text-[11px] font-medium", isActive ? "border-ac text-gd font-semibold" : "border-transparent text-mu")}>
                <n.icon className="h-5 w-5" />{n.label === "Overview" ? "Atención" : n.label}
              </NavLink>
            ))}
            <button onClick={() => setMore(true)} className="flex h-14 cursor-pointer flex-col items-center justify-center gap-0.5 border-t-2 border-transparent text-[11px] font-medium text-mu"><Menu className="h-5 w-5" />Más</button>
          </nav>
        </>
      )}

      {more && (
        <div className="fixed inset-0 z-40 flex items-end bg-black/55" onClick={() => setMore(false)}>
          <div onClick={(e) => e.stopPropagation()} className="slide-up safe-b w-full rounded-t-3xl border border-ln2 bg-sf p-4">
            <div className="mb-2 flex items-center justify-between px-2"><span className="font-display text-[18px]">Más</span><button onClick={() => setMore(false)} aria-label="Cerrar" className="flex h-11 w-11 items-center justify-center text-mu"><X className="h-5 w-5" /></button></div>
            {[NAV[1]!, NAV[5]!, NAV[6]!].map((n) => (
              <NavLink key={n.to} to={n.to} className="flex h-14 items-center gap-3 rounded-card px-3 text-[16px] hover:bg-hv"><n.icon className="h-5 w-5 text-mu" />{n.label}</NavLink>
            ))}
            <button onClick={toggle} className="flex h-14 w-full cursor-pointer items-center gap-3 rounded-card px-3 text-[16px] hover:bg-hv">{theme === "dark" ? <Sun className="h-5 w-5 text-mu" /> : <Moon className="h-5 w-5 text-mu" />}Tema {theme === "dark" ? "claro" : "oscuro"}</button>
            <button onClick={() => logout.mutate()} className="flex h-14 w-full cursor-pointer items-center gap-3 rounded-card px-3 text-[16px] text-er hover:bg-hv"><LogOut className="h-5 w-5" />Cerrar sesión ({me.data?.user.name})</button>
          </div>
        </div>
      )}
      <NotificationsPanel open={bell} onClose={() => setBell(false)} />
    </div>
  );
}
